import { useState } from "react";
import { Outlet, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Sidebar from "@/components/Sidebar";
import OnboardingWizard from "@/components/OnboardingWizard";
import HelpLauncher from "@/components/HelpLauncher";
import NotificationBell from "@/components/NotificationBell";
import VerifyEmailBanner from "@/components/VerifyEmailBanner";
import TrialBanner from "@/components/TrialBanner";
import TrialChip from "@/components/TrialChip";
import DunningBanner from "@/components/DunningBanner";

export default function Layout() {
  const { user, loading } = useAuth();
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-slatebody font-mono text-sm">{t("common.loading")}</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="flex min-h-screen bg-background">
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-backdrop"
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
        />
      )}
      <Sidebar mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
      <OnboardingWizard />
      <HelpLauncher />
      <main className="flex-1 min-w-0">
        <div className="md:hidden sticky top-0 z-30 flex items-center justify-between bg-[#F7F7F8] text-[#202123] border-b border-[#E5E5E5] px-4 py-3 shadow-sm">
          <button
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-btn"
            aria-label={t("nav.openMenu")}
            className="inline-flex items-center justify-center h-9 w-9 -ml-1 rounded-md hover:bg-[#ECECEC] transition-colors"
          >
            <Menu className="h-5 w-5" />
          </button>
          <img src="/jotbay-logo.png" alt="JotBay" className="h-7 w-auto" />
          <div className="flex items-center gap-2">
            <TrialChip />
            <NotificationBell />
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 py-6 md:py-10">
          <VerifyEmailBanner />
          <DunningBanner />
          <TrialBanner />
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export function ReadOnlyBanner() {
  const { user } = useAuth();
  if (user?.subscription_status !== "read_only") return null;
  // Payment-suspended paying accounts are covered by the global DunningBanner reactivate prompt;
  // don't also show the trial-ended copy (it would be misleading).
  if (user?.payment_suspended === true) return null;
  return (
    <div className="mb-6 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="readonly-banner">
      Your free trial has ended. You can view and export existing data, but adding new entries and generating reports requires a subscription.
    </div>
  );
}
